/**
 * useTokenHistory Hook
 *
 * Fetches token transaction history from the API.
 * Supports filtering by transaction type and pagination via limit.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { buildCloudApiUrl } from '../services/urlResolver';
import { isServerlessMode, isElectronMode } from '../utils/apiMode';

// ============================================================================
// Types
// ============================================================================

export interface TokenTransaction {
  type: string;
  amount: number;
  description: string;
  balance_after: number;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

interface UseTokenHistoryOptions {
  limit?: number;
  transactionType?: string | null;
}

interface UseTokenHistoryReturn {
  transactions: TokenTransaction[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useTokenHistory(options: UseTokenHistoryOptions = {}): UseTokenHistoryReturn {
  const { limit = 20, transactionType = null } = options;
  const { user, getIdToken } = useAuth();
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [hasMore, setHasMore] = useState(true);

  const fetchHistory = useCallback(
    async (fetchLimit: number) => {
      // Billing is centralized in the cloud — fetch history in serverless mode
      // and from Electron (desktop app also reaches the cloud API for tokens).
      if (!user || (!isServerlessMode() && !isElectronMode())) {
        setTransactions([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const token = await getIdToken();
        if (!token) {
          setTransactions([]);
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({ limit: String(fetchLimit) });
        if (transactionType) {
          params.set('transaction_type', transactionType);
        }

        const url = buildCloudApiUrl(`/api/v1/tokens/history?${params}`);
        console.log('[TokenHistory] Fetching:', url);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('[TokenHistory] Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          const fetched: TokenTransaction[] = data.data?.transactions ?? [];
          setTransactions(fetched);
          setHasMore(fetched.length >= fetchLimit);
        } else {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('[TokenHistory] Error response:', response.status, errorText);
          setError('Failed to fetch transaction history');
          setTransactions([]);
        }
      } catch (err) {
        console.error('[TokenHistory] Fetch failed:', err);
        setError('Failed to fetch transaction history');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    },
    [user, getIdToken, transactionType]
  );

  useEffect(() => {
    setCurrentLimit(limit);
    fetchHistory(limit);
  }, [fetchHistory, limit]);

  const refresh = useCallback(async () => {
    setCurrentLimit(limit);
    await fetchHistory(limit);
  }, [fetchHistory, limit]);

  const loadMore = useCallback(async () => {
    const newLimit = currentLimit + limit;
    setCurrentLimit(newLimit);
    await fetchHistory(newLimit);
  }, [currentLimit, limit, fetchHistory]);

  return {
    transactions,
    loading,
    error,
    refresh,
    loadMore,
    hasMore,
  };
}
